import React from 'react';
import { Box, Typography, Tooltip, useTheme } from '@ublend-npm/aulaui-core';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type { QuickLink } from '../constants/quicklinkConstants';

interface QuickLinkTileProps {
  link: QuickLink;
  role: 'student' | 'educator';
  isCompact?: boolean;
}

const QuickLinkTile: React.FC<QuickLinkTileProps> = ({
  link,
  role,
  isCompact,
}) => {
  const { label, circleBg, icon, href, iconSize } = link;
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const hrefKey = role === 'student' ? 'learner' : 'educator';
  const hrefByRole = href[hrefKey];

  const tileBg = theme.palette.background.default;
  const tileBgHover = isDark ? '#2C2733' : '#E4E1EA';
  const labelColor = theme.palette.text.secondary;

  const tileContent = (
    <Box
      onClick={() => window.open(hrefByRole, '_blank', 'noopener,noreferrer')}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          window.open(hrefByRole, '_blank', 'noopener,noreferrer');
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={label}
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        width: '100%',
        height: { xs: 100, sm: 112, md: 112, lg: 105, xl: 112 },
        boxSizing: 'border-box',
        borderRadius: '16px',
        pt: '12px',
        pb: '12px',
        px: '12px',
        bgcolor: tileBg,
        cursor: 'pointer',
        textDecoration: 'none !important',
        transition: 'background-color 0.15s ease',
        '&, &:hover, &:visited, &:active, &:link, &:focus': {
          textDecoration: 'none !important',
        },
        '&:hover': {
          bgcolor: tileBgHover,
        },
        '&:focus-visible': {
          outline: '2px solid #694AAD',
          outlineOffset: '2px',
        },
      }}
    >
      <Box
        sx={{
          width: iconSize ? `${iconSize}px` : 'clamp(38px, 6vw, 48px)',
          height: iconSize ? `${iconSize}px` : 'clamp(38px, 6vw, 48px)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          bgcolor: circleBg,
          overflow: 'hidden',
        }}
      >
        <img
          src={icon}
          alt=""
          style={{
            width: iconSize ? '100%' : '60%',
            height: iconSize ? '100%' : '60%',
            objectFit: 'contain',
            display: 'block',
          }}
        />
      </Box>

      <Typography
        component="span"
        sx={{
          width: '100%',
          fontWeight: 400,
          fontStyle: 'normal',
          fontSize: '0.875rem',
          lineHeight: '140%',
          letterSpacing: '0.17px',
          textAlign: 'center',
          color: `${labelColor} !important`,
          textDecoration: 'none !important',
          borderBottom: 'none !important',
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  if (!hrefByRole) return tileContent;

  return (
    <Tooltip
      title={
        <Box
          onClick={(e) => {
            e.stopPropagation();
            window.open(hrefByRole, '_blank', 'noopener,noreferrer');
          }}
          sx={{
            position: 'relative',
            maxWidth: '220px',
            pr: '20px',
            cursor: 'pointer',
          }}
        >
          <span>{hrefByRole}</span>
          <OpenInNewIcon
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              fontSize: '14px',
              flexShrink: 0,
            }}
          />
        </Box>
      }
      arrow
      placement="bottom-end"
      enterTouchDelay={0}
      slotProps={{
        popper: {
          disablePortal: true,
          sx: {
            pointerEvents: 'auto',
            zIndex: '1 !important',
          },
        },
      }}
    >
      {tileContent}
    </Tooltip>
  );
};

export default QuickLinkTile;
